import { CheckCircle2, ArrowRight, Users2, Package, AlertTriangle } from "lucide-react";

/**
 * Three-panel product showcase.
 *
 * Alternates left/right imagery vs. text — a pattern Linear/Framer use
 * heavily because each panel earns its own scroll beat. Every "mockup"
 * on this page is built from primitives (divs + Lucide icons) — no
 * external screenshot files, so page weight stays small and copy stays
 * evergreen when we ship UI changes.
 */
export default function ProductShowcase() {
  return (
    <section
      id="product"
      className="bg-primary text-primary-foreground scroll-mt-20"
      data-testid="landing-product"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28 space-y-24 md:space-y-32">
        {/* Panel 1 — Calendar */}
        <Panel
          eyebrow="// For the manager"
          title="A calendar that thinks like a dispatcher."
          body="Drag a job to reschedule. Assign to any staff. Color-coded by payment status. Multi-service bookings on a single card. Overlapping jobs? They stack side-by-side, no data hidden."
          bullets={[
            "Day · Week · Month views on desktop",
            "Rolling 4-day staff window on mobile",
            "Multi-template services per booking",
          ]}
          testId="panel-calendar"
          visual={<CalendarPanel />}
          reverse={false}
        />

        {/* Panel 2 — Invoicing */}
        <Panel
          eyebrow="// For the accountant"
          title="Invoices that follow Irish rules by default."
          body="VAT at 23%, RCT at 0%, cash-with-invoice, or simple cash — pick the payment type, JobsBook does the maths. Templates you design once, PDF delivery via Resend, audit trail on every state change."
          bullets={[
            "Automatic booking codes B-000001 · invoice numbers I-000001",
            "Per-tenant VAT rate, currency, and logo",
            "Aged debt & funds-by-method reports built-in",
          ]}
          testId="panel-invoicing"
          visual={<InvoicePanel />}
          reverse
        />

        {/* Panel 3 — Staff portal */}
        <Panel
          eyebrow="// For the crew"
          title="A phone URL, a PIN, and the jobs for today."
          body="No app store, no forgotten passwords. Each staff member gets /s/their-slug. They see the day, take a photo when they arrive, mark the job complete, log the cash. The manager sees it live."
          bullets={[
            "PIN-locked, per-staff sessions with expiry",
            "Photo evidence + notes attached to every job",
            "Cash tracked on staff ledger, settled at week's end",
          ]}
          testId="panel-staff"
          visual={<StaffPanel />}
          reverse={false}
        />

        {/* Panel 4 — Products & stock (P.15) */}
        <Panel
          eyebrow="// For the yard"
          title="Every part on every job, without leaving a receipt behind."
          body="Build your product catalog once. Staff attach parts to jobs from their phone — invoiced or off the books. Stock decrements automatically. Suggest new templates without leaving the job card."
          bullets={[
            "Per-usage: quantity, invoiced vs off-books, staff who used it",
            "Auto-added to the customer's invoice, never twice",
            "Low-stock warnings before you run out mid-call",
          ]}
          testId="panel-products"
          visual={<ProductsPanel />}
          reverse
        />
      </div>
    </section>
  );
}

function Panel({ eyebrow, title, body, bullets, visual, reverse, testId }) {
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center reveal ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={testId}
    >
      <div>
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">
          {eyebrow}
        </div>
        <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tight leading-[1.08]">
          {title}
        </h3>
        <p className="mt-4 text-base md:text-lg text-primary-foreground/70 leading-relaxed">
          {body}
        </p>
        <ul className="mt-6 space-y-2.5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-primary-foreground/80">
              <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>{visual}</div>
    </div>
  );
}

/* ------------ Visual sub-components ------------ */

function CalendarPanel() {
  const blocks = [
    { top: 12, left: 8,  w: 41, h: 44, tint: "bg-accent/90" },
    { top: 60, left: 8,  w: 41, h: 28, tint: "bg-emerald-400/80" },
    { top: 20, left: 52, w: 41, h: 60, tint: "bg-sky-400/80" },
    { top: 88, left: 52, w: 41, h: 28, tint: "bg-rose-400/80" },
    { top: 30, left: 30, w: 20, h: 34, tint: "bg-accent/70" },
  ];
  const hours = ["09", "10", "11", "12", "13", "14"];
  return (
    <div
      className="relative rounded-xl bg-card text-card-foreground border border-primary-foreground/10 shadow-2xl overflow-hidden"
      aria-hidden="true"
    >
      <div className="px-4 py-2.5 bg-secondary/60 border-b border-border flex items-center gap-2">
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Wed · 12 Feb · Day
        </span>
        <span className="ml-auto mono text-[10px] uppercase tracking-widest text-emerald-700">
          3 · in progress
        </span>
      </div>
      <div className="relative h-[280px] p-3">
        <div className="absolute inset-3 grid grid-cols-1 gap-0">
          {hours.map((h) => (
            <div key={h} className="flex items-start gap-3 border-t border-border/60 py-0.5">
              <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground w-6">
                {h}
              </span>
            </div>
          ))}
        </div>
        {blocks.map((b, i) => (
          <div
            key={i}
            className={`absolute rounded-md border-l-[3px] border-accent ${b.tint} shadow-md`}
            style={{
              top: `${b.top}%`,
              left: `${b.left}%`,
              width: `${b.w}%`,
              height: `${b.h}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function InvoicePanel() {
  return (
    <div
      className="relative rounded-xl bg-card text-card-foreground border border-primary-foreground/10 shadow-2xl p-6"
      aria-hidden="true"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Invoice
          </div>
          <div className="font-black text-2xl mt-1">I-000147</div>
        </div>
        <div className="text-right">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Total
          </div>
          <div className="font-black text-2xl mt-1 text-accent">€482.00</div>
        </div>
      </div>
      <div className="mt-6 space-y-2 text-sm">
        <Row k="Customer"     v="Foxford Civils Ltd" />
        <Row k="Booking"      v="B-000432 · Pump hire" mono />
        <Row k="Payment type" v="Cash with invoice" />
        <Row k="VAT (23%)"    v="€90.16" mono />
        <Row k="Subtotal"     v="€391.87" mono />
      </div>
      <div className="mt-5 pt-4 border-t border-border flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded">
          <CheckCircle2 className="h-3 w-3" /> paid · cash to driver
        </span>
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground ml-auto">
          John Doyle · 12 Feb
        </span>
      </div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? "mono font-bold" : "font-bold"}>{v}</span>
    </div>
  );
}

function StaffPanel() {
  const jobs = [
    { code: "B-000432", cust: "Murphy Construction", time: "09:00 – 13:00", status: "done" },
    { code: "B-000433", cust: "Kilkenny Civils",     time: "14:00 – 15:30", status: "in_progress" },
    { code: "B-000434", cust: "Ryan Farm Supplies",  time: "16:30 – 17:15", status: "upcoming" },
  ];
  const statusTint = {
    done:         "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_progress:  "bg-amber-50 text-amber-800 border-amber-200",
    upcoming:     "bg-muted text-muted-foreground border-border",
  };
  return (
    <div className="mx-auto max-w-[360px] rounded-[2rem] bg-primary border-8 border-primary-foreground/10 shadow-2xl overflow-hidden">
      <div className="bg-card text-card-foreground rounded-[1.4rem] m-1">
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-accent grid place-items-center text-accent-foreground font-black">
            JD
          </div>
          <div>
            <div className="font-black text-sm">John Doyle</div>
            <div className="mono text-[10px] text-muted-foreground">Wednesday · 12 Feb</div>
          </div>
          <Users2 className="h-4 w-4 text-muted-foreground ml-auto" />
        </div>
        <div className="p-4 space-y-3">
          {jobs.map((j) => (
            <div
              key={j.code}
              className="border border-border rounded-lg p-3 hover:border-accent/60 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-black text-sm truncate">{j.cust}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {j.code} · {j.time}
                  </div>
                </div>
                <span className={`mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${statusTint[j.status]}`}>
                  {j.status.replace("_", " ")}
                </span>
              </div>
              {j.status === "in_progress" && (
                <button
                  type="button"
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-accent text-accent-foreground text-xs font-bold py-2 rounded-md"
                >
                  Mark completed <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductsPanel() {
  const rows = [
    { name: "Pump Filter Cartridge", sku: "PFC-42", stock: 48, qty: 2, price: 25.00, mode: "invoiced" },
    { name: "Copper Union 22mm",     sku: "CU-22",  stock: 12, qty: 1, price:  8.50, mode: "invoiced" },
    { name: "Grease (courtesy)",     sku: "GR-25",  stock:  3, qty: 1, price:  0.00, mode: "off_books", low: true },
  ];
  return (
    <div
      className="relative rounded-xl bg-card text-card-foreground border border-primary-foreground/10 shadow-2xl overflow-hidden"
      aria-hidden="true"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/60 border-b border-border">
        <Package className="h-4 w-4 text-accent" />
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          products on booking B-000432
        </span>
        <span className="ml-auto mono text-[10px] uppercase tracking-widest text-emerald-700">3 attached</span>
      </div>
      <div className="p-4 space-y-2">
        {rows.map((r) => (
          <div
            key={r.sku}
            className="border border-border rounded-lg px-3 py-2.5 flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-md bg-accent/10 text-accent grid place-items-center shrink-0">
              <Package className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black text-sm truncate">{r.name}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {r.sku} · {r.qty}× · stock {r.stock}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="mono tabular-nums font-bold text-sm">
                {r.mode === "off_books" ? "—" : `€${(r.price * r.qty).toFixed(2)}`}
              </div>
              <div className="flex items-center gap-1 mt-0.5 justify-end">
                {r.low && <AlertTriangle className="h-3 w-3 text-amber-600" />}
                <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${r.mode === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground border-border"}`}>
                  {r.mode === "invoiced" ? "invoiced" : "off-books"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4 pt-1 flex items-center justify-between border-t border-border mt-2">
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          added to invoice on regenerate
        </span>
        <span className="mono tabular-nums font-black text-sm">+ €58.50</span>
      </div>
    </div>
  );
}

